<template>
	<a-modal
		:visible="visible"
		:footer="null"
		:closable="false"
		:mask-closable="false"
		:destroy-on-close="true"
		width="640px"
		:centered="true"
		wrap-class-name="g-audio-settings-modal"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
		<div class="g-audio-settings">
			<div class="g-audio-settings__header">
				<span class="g-audio-settings__title">音频模块</span>
				<span class="g-audio-settings__close mbm-iconfont mbm-guanbi1" @click="onCancel"></span>
			</div>

			<UploadProgressPanel
				v-if="isUploading"
				:tasks="uploadTasks"
				variant="modal"
			/>
			<div v-else class="g-audio-settings__player-box">
				<HeaderAudioPreview
					v-if="currentItem"
					:item="currentItem"
					layout="inline"
				/>
			</div>

			<div class="g-audio-settings__toolbar">
				<button
					type="button"
					class="g-audio-settings__outline-btn"
					:disabled="isUploading"
					@click="onReupload"
				>
					重新上传
				</button>
			</div>

			<p v-if="!hideNotice" class="g-audio-settings__notice">
				高清流量使用完后，音频将无法播放。
				<a href="javascript:;" class="g-audio-settings__notice-link">查看说明</a>
			</p>

			<div class="g-audio-settings__footer">
				<div class="g-audio-settings__footer-actions">
					<button type="button" class="g-audio-settings__btn is-cancel" @click="onCancel">取消</button>
					<button
						type="button"
						class="g-audio-settings__btn is-confirm"
						:disabled="!form.items.length || isUploading"
						@click="onConfirm"
					>确认</button>
				</div>
			</div>
		</div>
		</div>

		<input
			ref="fileInput"
			type="file"
			accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
			class="g-audio-settings__file-input"
			@change="onFileChange"
		/>
	</a-modal>
</template>

<script>
	import HeaderAudioPreview from './HeaderAudioPreview.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import {
		filterValidLocalAudioList,
		normalizeHeaderAudios,
		normalizeLocalAudios,
		revokeLocalAudioItem,
		revokeLocalAudios,
	} from '../utils/localAudio.js';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GAudioSettingsModal',
		components: { HeaderAudioPreview, UploadProgressPanel },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			value: {
				type: Object,
				default: () => ({
					items: [],
				}),
			},
			hideNotice: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: {
					items: [],
				},
				skipNextInit: false,
				isUploading: false,
				uploadTasks: [],
			};
		},
		computed: {
			currentItem() {
				return this.form.items[0] || null;
			},
		},
		watch: {
			visible(value) {
				if (value) {
					if (this.skipNextInit) {
						this.skipNextInit = false;
						return;
					}
					this.initForm();
				}
			},
		},
		beforeUnmount() {
			revokeLocalAudios(this.form.items);
		},
		methods: {
			initForm() {
				const source = normalizeHeaderAudios(this.value);
				const incoming = source.items;
				const incomingIds = new Set(incoming.map((item) => item.id));
				this.form.items.forEach((item) => {
					if (!incomingIds.has(item.id)) {
						revokeLocalAudioItem(item);
					}
				});
				this.form = {
					items: incoming,
				};
			},
			async setAudioFile(file) {
				const validFiles = filterValidLocalAudioList([file]);
				if (!validFiles.length) return;
				await this.uploadFiles(validFiles, (items) => {
					revokeLocalAudios(this.form.items);
					this.form.items = items;
				});
			},
			async uploadFiles(files, onSuccess) {
				this.isUploading = true;
				this.uploadTasks = createUploadTasks(files);
				try {
					const items = await uploadHeaderMediaFiles(this, {
						type: 'audio',
						files,
						onProgress: (payload) => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					onSuccess?.(items);
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			onReupload() {
				this.$refs.fileInput.value = '';
				this.$refs.fileInput.click();
			},
			async onFileChange(event) {
				const file = (event.target.files || [])[0];
				event.target.value = '';
				if (!file) return;
				await this.setAudioFile(file);
			},
			async openWithFile(file) {
				const validFiles = filterValidLocalAudioList([file]);
				if (!validFiles.length) return;
				revokeLocalAudios(this.form.items);
				this.form = { items: [] };
				this.skipNextInit = true;
				this.$emit('update:visible', true);
				await this.uploadFiles(validFiles, (items) => {
					this.form.items = items;
				});
			},
			openWithUploadedItems(items) {
				revokeLocalAudios(this.form.items);
				this.form = {
					items: normalizeLocalAudios(items),
				};
				this.skipNextInit = true;
				this.$emit('update:visible', true);
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				if (!this.form.items.length) return;
				this.$emit('confirm', normalizeHeaderAudios(this.form));
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	.g-audio-settings__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;
	}

	.g-audio-settings__title {
		font-size: 18px;
		font-weight: 600;
		color: #333;
	}

	.g-audio-settings__close {
		font-size: 16px;
		color: #999;
		cursor: pointer;
		line-height: 1;

		&:hover {
			color: #666;
		}
	}

	.g-audio-settings__player-box {
		border: 1px solid #eee;
		border-radius: 4px;
		background: #fff;
		overflow: hidden;
	}

	.g-audio-settings__toolbar {
		display: flex;
		align-items: center;
		margin-top: 14px;
	}

	.g-audio-settings__outline-btn {
		height: 32px;
		padding: 0 14px;
		border: 1px solid #d9d9d9;
		border-radius: 4px;
		background: #fff;
		font-size: 13px;
		color: #666;
		cursor: pointer;

		&:hover {
			border-color: #285af9;
			color: #285af9;
		}
	}

	.g-audio-settings__notice {
		margin: 14px 0 0;
		font-size: 12px;
		color: #999;
		line-height: 1.6;
	}

	.g-audio-settings__notice-link {
		color: #285af9;
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}

	.g-audio-settings__footer {
		display: flex;
		justify-content: flex-end;
		margin-top: 24px;
		padding-top: 16px;
		border-top: 1px solid #f0f0f0;
	}

	.g-audio-settings__footer-actions {
		display: flex;
		gap: 10px;
	}

	.g-audio-settings__btn {
		min-width: 72px;
		height: 32px;
		padding: 0 16px;
		border-radius: 4px;
		font-size: 14px;
		cursor: pointer;
		outline: none;

		&.is-cancel {
			border: 1px solid #d9d9d9;
			background: #fff;
			color: #333;

			&:hover {
				color: #5280ff;
    			border-color: #5280ff;
			}
		}

		&.is-confirm {
			border: none;
			background: #285af9;
			color: #fff;

			&:hover:not(:disabled) {
				background: #3868ff;
			}

			&:disabled {
				background: #3868ff;
				cursor: not-allowed;
			}
		}
	}

	.g-audio-settings__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-audio-settings-modal {
		.edit-modal-scroll();

		.ant-modal-body {
			padding: 20px 24px 18px;
		}
	}
</style>
